export interface IngredientCalculationParams {
  quantity?: number | null
  multiplierEat?: number
  multiplierFreezer?: number
  portionsEat?: number
  portionsFreezer?: number
  isFreezable?: boolean
}

/**
 * Calculates ingredient total quantity based on meal planning parameters
 * @param params - The calculation parameters
 * @returns Total calculated quantity for the ingredient
 */
export function calculateIngredientQuantities(
  params: IngredientCalculationParams
): number {
  let {
    quantity = 0,
    multiplierEat = 1,
    multiplierFreezer = 1,
    portionsEat = 2,
    portionsFreezer = 0,
    isFreezable = false,
  } = params

  const portions = portionsEat + portionsFreezer

  let eatActual = multiplierEat * portionsEat
  let freezerActual = multiplierFreezer * portionsFreezer

  if (multiplierFreezer < 0) {
    return isFreezable ? 0 : (quantity! / portionsEat) * -freezerActual
  }

  if (isFreezable) {
    return ((eatActual + freezerActual) * quantity!) / portions
  }

  return (eatActual * quantity!) / portionsEat
}

/**
 * Formats a number for display using Dutch/Belgian locale
 * @param value - The number to format
 * @returns Formatted number string
 */
export function formatQuantity(value: number): string {
  return value.toLocaleString('nl-BE', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })
}

/**
 * Checks if an ingredient should be displayed based on its calculated quantity
 * @param totalQuantity - The calculated total quantity
 * @returns True if the ingredient should be displayed
 */
export function shouldDisplayIngredient(totalQuantity: number): boolean {
  return totalQuantity > 0
}

/**
 * Helper function to extract and apply default values from plan and recipe data
 * This matches the default handling patterns used in PlanDetail component
 */
export function getIngredientCalculationParams(
  ingredient: { quantity: number | null; freezer: boolean | null },
  plan?: { multiplier?: number | null; multiplierFreezer?: number | null },
  recipe?: { portionsEat?: number | null; portionsFreezer?: number | null }
): IngredientCalculationParams {
  return {
    quantity: ingredient.quantity || 0,
    multiplierEat: plan?.multiplier ?? 1, // matches: plan?.multiplier ?? 1
    multiplierFreezer: plan?.multiplierFreezer ?? 1, // matches: plan?.multiplierFreezer ?? 1
    portionsEat: recipe?.portionsEat ?? 2, // matches: recipe?.portionsEat ?? 2
    portionsFreezer: recipe?.portionsFreezer ?? 0, // matches: recipe?.portionsFreezer ?? 0
    isFreezable: ingredient.freezer || false,
  }
}

/**
 * Calculates ingredient quantities for all ingredients in a recipe based on plan multipliers
 * @param plan - The plan containing multipliers
 * @param recipe - The full recipe with ingredients
 * @returns Array of ingredients with calculated quantities
 */
export function calculateRecipeIngredientQuantities<
  T extends {
    id: number
    productId: number | null
    quantity: number | null
    freezer: boolean | null
    product: any
  },
>(
  plan: {
    multiplier?: number | null
    multiplierFreezer?: number | null
  },
  recipe: {
    portionsEat?: number | null
    portionsFreezer?: number | null
    ingredients: T[]
  }
): Array<T & { quantity: number }> {
  return recipe.ingredients
    .map((ingredient) => {
      const totalQuantity = calculateIngredientQuantities({
        quantity: ingredient.quantity || 0,
        multiplierEat: plan.multiplier ?? 1,
        multiplierFreezer: plan.multiplierFreezer ?? 1,
        portionsEat: recipe.portionsEat ?? 2,
        portionsFreezer: recipe.portionsFreezer ?? 0,
        isFreezable: ingredient.freezer || false,
      })
      // console.log('totalQuantity', ingredient.product.display, totalQuantity)

      return {
        ...ingredient,
        quantity: totalQuantity,
      }
    })
    .filter((ingredient) => ingredient.quantity > 0)
}
